'use client';

import { cn } from '@/utils/styles';
import { isTextSelection } from '@tiptap/core';
import { BubbleMenu, BubbleMenuProps } from '@tiptap/react';
import { Heading1, Heading2, Heading3, Link, List, Underline } from 'lucide-react';
import { Bold, Italic } from 'lucide-react';
import { ListOrdered } from 'lucide-react';

import { useCallback, useMemo, useRef } from 'react';

import { Toggle } from '@/components/ui/toggle';

import { EditLinkPopover } from '../link/edit-popover';
import LinkMenu from '../link/link-menu';
import { RTE_EXTENSIONS } from '../types/rich-text-editor';
import { IToolbarProps } from './toolbar.types';

// Map toolbar option keys to their required extensions
const OPTION_TO_EXTENSION: Record<string, RTE_EXTENSIONS | null> = {
	bold: RTE_EXTENSIONS.STARTER_KIT, // bold is part of starter kit
	italic: RTE_EXTENSIONS.STARTER_KIT, // italic is part of starter kit
	underline: RTE_EXTENSIONS.UNDERLINE,
	link: RTE_EXTENSIONS.LINK,
	heading: RTE_EXTENSIONS.HEADING,
	heading2: RTE_EXTENSIONS.HEADING,
	heading3: RTE_EXTENSIONS.HEADING,
	list: RTE_EXTENSIONS.BULLET_LIST,
	'list-ordered': RTE_EXTENSIONS.ORDERED_LIST,
};

function ToolBar({ editor, allowedExtensions, hiddenToolbarOptions }: IToolbarProps) {
	const menuRef = useRef<HTMLDivElement>(null);

	/** Mirrors the BubbleMenu default, minus node selections — an image has no marks to toggle. */
	const shouldShow = useCallback<NonNullable<BubbleMenuProps['shouldShow']>>(
		({ view, state, from, to }) => {
			const { doc, selection } = state;
			if (!isTextSelection(selection) || selection.empty) return false;
			if (!doc.textBetween(from, to).length) return false;

			const isChildOfMenu = menuRef.current?.contains(document.activeElement) ?? false;
			return (view.hasFocus() || isChildOfMenu) && view.editable;
		},
		[]
	);

	const handleSetLink = (url: string) => {
		if (!editor) return;
		if (url === '') {
			editor.chain().focus().unsetLink().run();
		} else {
			const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`);

			editor.chain().focus().extendMarkRange('link').setLink({ href: parsedUrl.toString() }).run();
		}
	};

	// Filter options based on allowedExtensions (if provided)
	const filteredOptions = useMemo(() => {
		if (!editor) return [];

		const options = [
			{
				key: 'bold',
				icon: <Bold size="16" />,
				onClick: () => editor.chain().focus().toggleBold().run(),
				pressed: editor.isActive('bold'),
			},
			{
				key: 'italic',
				icon: <Italic size="16" />,
				onClick: () => editor.chain().focus().toggleItalic().run(),
				pressed: editor.isActive('italic'),
			},
			{
				key: 'underline',
				icon: <Underline size="16" />,
				onClick: () => editor.chain().focus().toggleUnderline().run(),
				pressed: editor.isActive('underline'),
			},
			{
				key: 'link',
				type: 'Link',
				icon: <Link size="16" />,
				onClick: () => {},
				pressed: editor.isActive('link'),
			},
			{
				key: 'heading',
				icon: <Heading1 size="16" />,
				onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
				pressed: editor.isActive('heading', { level: 1 }),
			},
			{
				key: 'heading2',
				icon: <Heading2 size="16" />,
				onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
				pressed: editor.isActive('heading', { level: 2 }),
			},
			{
				key: 'heading3',
				icon: <Heading3 size="16" />,
				onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
				pressed: editor.isActive('heading', { level: 3 }),
			},
			{
				key: 'list',
				icon: <List size="16" />,
				onClick: () => editor.chain().focus().toggleBulletList().run(),
				pressed: editor.isActive('bulletList'),
			},
			{
				key: 'list-ordered',
				icon: <ListOrdered size="16" />,
				onClick: () => editor.chain().focus().toggleOrderedList().run(),
				pressed: editor.isActive('orderedList'),
			},
		];

		// Filter by allowed extensions
		let filtered = options;
		if (allowedExtensions) {
			filtered = filtered.filter((option) => {
				const requiredExtension = OPTION_TO_EXTENSION[option.key];
				return requiredExtension ? allowedExtensions.includes(requiredExtension) : true;
			});
		}

		// Filter out explicitly hidden options
		if (hiddenToolbarOptions?.length) {
			filtered = filtered.filter((option) => !hiddenToolbarOptions.includes(option.key));
		}

		return filtered;
	}, [editor, allowedExtensions, hiddenToolbarOptions]);

	if (!editor) return null;

	return (
		<>
			<BubbleMenu editor={editor} shouldShow={shouldShow}>
				<div
					ref={menuRef}
					className="sticky top-10 z-50 mb-1 flex w-fit space-x-1 rounded-lg border bg-white p-1.5"
				>
					{filteredOptions.map((option) => {
						if (option.type === 'Link') {
							return (
								<div key={option.key}>
									{/* Don't remove this extra div, it needs for parent referencing in toggle */}
									<div>
										<EditLinkPopover
											isPressed={option.pressed}
											initialUrl={editor.getAttributes('link').href ?? ''}
											onSetLink={handleSetLink}
										/>
									</div>
									<LinkMenu editor={editor} />
								</div>
							);
						}
						if (!option.type) {
							return (
								<Toggle
									key={option.key}
									size="sm"
									pressed={option.pressed}
									onPressedChange={option.onClick}
									className={cn(
										'hover:bg-accent hover:text-accent-foreground data-[state=on]:bg-accent',
										'transition-colors',
										'text-slate-700 data-[state=on]:text-accent-foreground'
									)}
								>
									{option.icon}
								</Toggle>
							);
						}
					})}
				</div>
			</BubbleMenu>
		</>
	);
}

export default ToolBar;
